import { useEffect, useMemo, useRef } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import {
  RouteStepPagerRow,
  STEP_ROW_HEIGHT,
} from '@/components/route-planner/RouteStepPagerRow';
import type { RouteLeg, RoutePlan, RoutePlanStop } from '@/types/route-plan';

const VISIBLE_ROWS = 3;
const PAGER_HEIGHT = STEP_ROW_HEIGHT * VISIBLE_ROWS;
const FADE_HEIGHT = STEP_ROW_HEIGHT * 0.75;

type RouteStepPagerProps = {
  plan: RoutePlan;
  currentLegIndex: number;
  stopsById: Map<string, RoutePlanStop>;
  completedLegIds: string[];
  onToggle: (legId: string) => void;
  onSkip: (legId: string) => void;
};

/**
 * Departure guidance for the current step: when the current or following
 * leg is a travel leg, say when to leave.
 */
function buildGuidance(legs: RouteLeg[], currentIndex: number): string | null {
  const current = legs[currentIndex];
  if (!current) return null;
  if (current.kind === 'travel') return `leave ${current.departTime}`;
  const next = legs[currentIndex + 1];
  if (next && next.kind === 'travel') return `next: leave ${next.departTime}`;
  return null;
}

/**
 * RouteStepPager — the home card's step navigator: a three-row viewport over
 * the day's numbered steps, free-scrolling, centered on the current step,
 * with gradient fades above and below.
 */
export function RouteStepPager({
  plan,
  currentLegIndex,
  stopsById,
  completedLegIds,
  onToggle,
  onSkip,
}: RouteStepPagerProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const scrollRef = useRef<ScrollView>(null);
  const completedSet = useMemo(() => new Set(completedLegIds), [completedLegIds]);

  // Center the current step whenever it changes (all steps done → last step).
  // The contentContainer's vertical padding equals the centering offset, so
  // scrolling to targetIndex * STEP_ROW_HEIGHT puts the row mid-viewport.
  useEffect(() => {
    const targetIndex = currentLegIndex >= 0 ? currentLegIndex : plan.legs.length - 1;
    scrollRef.current?.scrollTo({ y: targetIndex * STEP_ROW_HEIGHT, animated: true });
  }, [currentLegIndex, plan.legs.length]);

  const fadeColor = palette.background;

  return (
    <View style={styles.viewport}>
      <ScrollView
        ref={scrollRef}
        nestedScrollEnabled
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {plan.legs.map((leg, index) => (
          <RouteStepPagerRow
            key={leg.id}
            leg={leg}
            stop={leg.kind === 'stay' ? stopsById.get(leg.stopId) : undefined}
            stepNumber={index + 1}
            isCompleted={completedSet.has(leg.id)}
            isCurrent={index === currentLegIndex}
            isLast={index === plan.legs.length - 1}
            animateConnector={index === currentLegIndex - 1}
            guidance={index === currentLegIndex ? buildGuidance(plan.legs, currentLegIndex) : null}
            onToggle={onToggle}
            onSkip={onSkip}
          />
        ))}
      </ScrollView>
      <LinearGradient
        colors={[fadeColor, `${fadeColor}00`]}
        style={[styles.fade, styles.fadeTop]}
        pointerEvents="none"
      />
      <LinearGradient
        colors={[`${fadeColor}00`, fadeColor]}
        style={[styles.fade, styles.fadeBottom]}
        pointerEvents="none"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  viewport: {
    height: PAGER_HEIGHT,
  },
  scrollContent: {
    paddingVertical: (PAGER_HEIGHT - STEP_ROW_HEIGHT) / 2,
  },
  fade: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: FADE_HEIGHT,
  },
  fadeTop: {
    top: 0,
  },
  fadeBottom: {
    bottom: 0,
  },
});
